import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import useTeacherAIAnalyticsPanel from '../views/TeacherAIAnalyticsPanelView/useTeacherAIAnalyticsPanel';

type SelectOption = {
  label: string;
  value: string;
};

type AnalyticsFiltersContextValue = {
  allCourseIds: string[];
  classOptions: SelectOption[];
  filtersReady: boolean;
  groupCourseMap: Record<string, string[]>;
  loading: boolean;
  selectedClass: SelectOption;
  selectedTimePeriod: SelectOption;
  setSelectedClass: (option: SelectOption) => void;
  setSelectedTimePeriod: (option: SelectOption) => void;
  timePeriodOptions: SelectOption[];
};

const AnalyticsFiltersContext = createContext<AnalyticsFiltersContextValue | null>(null);

function AnalyticsFiltersProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const { classOptions, selectedClass, setSelectedClass, timePeriodOptions, selectedTimePeriod, setSelectedTimePeriod, allCourseIds, groupCourseMap, loading } =
    useTeacherAIAnalyticsPanel();

  const location = useLocation();
  const history = useHistory();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const filtersInitialized = useRef(false);
  const [filtersReady, setFiltersReady] = useState(false);

  // Initialize filters from URL query params once options are available
  useEffect(() => {
    if (loading || filtersInitialized.current) return;

    const classParam = queryParams.get('class');
    const periodParam = queryParams.get('period');

    if (classParam) {
      const matchingClass = classOptions.find((opt) => opt.value === classParam);
      if (matchingClass) setSelectedClass(matchingClass);
    }

    if (periodParam) {
      const matchingPeriod = timePeriodOptions.find((opt) => opt.value === periodParam);
      if (matchingPeriod) setSelectedTimePeriod(matchingPeriod);
    }

    filtersInitialized.current = true;
    setFiltersReady(true);
  }, [loading, classOptions, timePeriodOptions, queryParams, setSelectedClass, setSelectedTimePeriod]);

  // Sync filter state to URL query params
  useEffect(() => {
    if (!filtersInitialized.current) return;

    const params = new URLSearchParams();
    params.set('class', selectedClass.value);
    params.set('period', selectedTimePeriod.value);

    const newSearch = `?${params.toString()}`;

    if (location.search !== newSearch) {
      history.replace({ pathname: location.pathname, search: newSearch });
    }
  }, [selectedClass.value, selectedTimePeriod.value, history, location.pathname, location.search]);

  const value = useMemo(
    () => ({
      allCourseIds,
      classOptions,
      filtersReady,
      groupCourseMap,
      loading,
      selectedClass,
      selectedTimePeriod,
      setSelectedClass,
      setSelectedTimePeriod,
      timePeriodOptions,
    }),
    [
      allCourseIds,
      classOptions,
      filtersReady,
      groupCourseMap,
      loading,
      selectedClass,
      selectedTimePeriod,
      setSelectedClass,
      setSelectedTimePeriod,
      timePeriodOptions,
    ]
  );

  return <AnalyticsFiltersContext.Provider value={value}>{children}</AnalyticsFiltersContext.Provider>;
}

function useAnalyticsFilters(): AnalyticsFiltersContextValue {
  const context = useContext(AnalyticsFiltersContext);
  if (!context) {
    throw new Error('useAnalyticsFilters must be used within an AnalyticsFiltersProvider');
  }
  return context;
}

export { AnalyticsFiltersProvider, useAnalyticsFilters };
